import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { dset } from 'dset'
import delve from 'dlv'
import qs from 'query-string'
import Cookies from 'js-cookie'
import { connect } from 'react-redux'
import { reduxForm, SubmissionError } from 'redux-form'
import { Alert } from '../LegacyComponents'
import Icon from '../Icon'
import storage from '@mylo/webcore/es/utility/localStorage'
import strings from './strings'

import { hasString, hasArray, getActionCreators } from './helpers'
import debounce from '../../utility/debounce'
import { timedSubmit } from '../../utility/schemaHelpers'
import formatPhoneNumber from '../../utility/formatPhoneNumber'

import { MuiSchemaQuestions } from './MuiSchemaQuestions'
import { evaluateCondition } from './evaluateCondition'
import { Typography, Button } from '@mui/material'
import { ContentWrapper } from './ContentWrapper'

import './MuiBuyflow.css'
import Spinner from '../../../common/components/Spinner'

class MuiBuyflow extends Component {
  static propTypes = {
    currentValues: PropTypes.object,
    support: PropTypes.string,
    confirmation: PropTypes.shape({
      title: PropTypes.string,
      message: PropTypes.string,
      redirect: PropTypes.string,
      redirectText: PropTypes.string,
      data: PropTypes.object
    }),
    isAdmin: PropTypes.bool,
    handleSubmit: PropTypes.func.isRequired,
    submitting: PropTypes.bool,
    error: PropTypes.string,
    schema: PropTypes.shape({
      form: PropTypes.string.isRequired,
      hooks: PropTypes.object,
      title: PropTypes.string,
      logo: PropTypes.string,
      pretext: PropTypes.string,
      infoText: PropTypes.string,
      sections: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          pretext: PropTypes.string,
          fields: PropTypes.arrayOf(
            PropTypes.shape({
              name: PropTypes.string.isRequired,
              component: PropTypes.string.isRequired,
              label: PropTypes.string
            })
          )
        })
      )
    })
  }

  static defaultProps = {
    confirmation: {},
    isAdmin: false
  }

  state = {
    unsubscribe: null
  }

  componentDidMount () {
    const { schema, store } = this.props

    if (typeof schema !== 'object') {
      throw new Error('Expected `schema` prop to be an object')
    }

    if (typeof store !== 'object' || typeof store.dispatch !== 'function') {
      throw new Error('Expected `store` prop to be a redux store')
    }

    const args = delve(schema, 'hooks.observeStore', {})
    const fallback = () => {}
    const handler = delve(window, '__mylo__.observeStore', fallback)
    const actionCreators = getActionCreators()
    const props = { actionCreators, schema }

    const subFn = () =>
      handler(store.getState(), store.dispatch, props, args, window)

    let sub = subFn
    if (!isNaN(args.debounceMs)) {
      sub = debounce(subFn, args.debounceMs)
    }

    const unsubscribe = store.subscribe(sub)

    // invoke manually
    handler(
      store.getState(),
      store.dispatch,
      { ...props, lifecycle: 'componentDidMount' },
      args,
      window
    )

    this.setState(() => ({ unsubscribe }))
  }

  componentWillUnmount () {
    if (this.state.unsubscribe) {
      this.state.unsubscribe()
    }
  }

  render () {
    const {
      submitAttributes,
      submitText,
      support,
      confirmation,
      handleSubmit,
      error,
      schema,
      submitting,
      isAdmin
    } = this.props

    return (
      <ContentWrapper maxWidth={schema.widerForm ? false : 'sm'} paddingTop={4} paddingTopMobile={4}
        className={
          schema.widerForm
            ? 'wider-form'
            : isAdmin ? 'admin-wrapper' : ''
        }
      >
        <div className={schema.className}>
          <div className='section--headline'>
            <Typography variant='h3' color='textSecondary' style={{ fontWeight: 500 }}>{schema.title}</Typography>
          </div>
          {submitting ? (
            <Fragment>
              <p className="content--center">{strings.PAGE__WORKING}</p>
              <Spinner />
            </Fragment>
          ) : (hasString(confirmation.message) ? (
            <div className="notification">
              <Icon
                iconName="check"
                className="notification__icon"
                size="mid-large"
              />
              <div className="notification__content">
                {hasString(confirmation.title) && (
                  <h3>{confirmation.title}</h3>
                )}
                <p>{confirmation.message}</p>
                {hasString(confirmation.redirect) &&
                      hasString(confirmation.redirectText) && (
                  <a
                    href={confirmation.redirect}
                    rel="noopener"
                  >
                    {confirmation.redirectText}
                  </a>
                )}
                {hasString(confirmation.responseId) && (
                  <div hidden value={confirmation.responseId} />
                )}
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate>
              {hasString(schema.logo) &&
                  hasString(schema.title) && (
                <h3>{schema.title}</h3>
              )}
              {hasString(schema.pretext) && (
                <p>{schema.pretext}</p>
              )}
              {hasArray(schema.sections) && (
                <MuiSchemaQuestions
                  sections={schema.sections}
                  currentValues={this.props.currentValues}
                />
              )}
              <div className='section--submit-button'>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  id='submit'
                  {...submitAttributes}>
                  {submitText}
                </Button>
              </div>
              {hasString(schema.infoText) && (
                <div className='info-text'>
                  <Typography variant="body1" style={{ fontWeight: 400 }}>
                    {schema.infoText}
                  </Typography>
                </div>
              )}
              {!!error && (
                <Alert type="error" className="grid-cell buyflow__error">
                  <span className="cell-align--center">
                    {error || 'Please correct any errors and retry!'}
                    {support && (
                      <Fragment>
                        {' '}
                        <a href={support}>Contact Support</a>
                      </Fragment>
                    )}
                  </span>
                </Alert>
              )}

            </form>
          )
          )}
        </div>
      </ContentWrapper>
    )
  }
}

export function extractSupportMeta ({ support, error, values }) {
  if (typeof support !== 'object') {
    return
  }

  let subject = typeof support.subject === 'string' ? support.subject : ''
  while (/\{\{/.test(subject)) {
    subject = subject.replace(/\{\{(.+?)\}\}/, (match, path) =>
      // look for the path in error first, then in values
      delve(error, path, delve(values, path, ''))
    )
  }

  return {
    ...support,
    subject
  }
}

export function extractSupportLink (state, formName) {
  const { to, subject } = delve(
    state,
    `form.${formName}.submitErrors._support`,
    {}
  )
  if (!to || !subject) {
    return null
  }
  return `mailto:${to}?subject=${subject}`
}

export function extractConfirmation (state, formName) {
  return delve(state, `form.${formName}.confirmation`, {})
}

export function mapStateToProps (
  state,
  {
    schema: {
      form,
      sections = [],
      submit = {},
      hooks: { onSubmit: onSubmitArgs }
    } = {}
  }
) {
  const formName = form || 'buyflow'
  const existingValues = delve(state, `form.${formName}.values`, {})
  const schemaValues = sections.reduce((data, section) => {
    if (Array.isArray(section.fields)) {
      return section.fields.reduce((data, field) => {
        if (Array.isArray(field.fields)) {
          return prefillValues(data, field.fields)
        }
        if (
          typeof field.value === 'object' &&
          field.value.hasOwnProperty('from')
        ) {
          return prefillValue(data, field)
        }
        if (field.value) {
          dset(data, field.name, field.value)
          return data
        }
        return data
      }, data)
    }
    return data
  }, {})
  const initialValues = {
    ...schemaValues,
    ...existingValues
  }
  const validate = (values, props) => {
    const validateFn = delve(window, `__mylo__.validate`, () => ({}))
    return validateFn(values, props)
  }
  const onSubmit = (values, dispatch, props) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    const onSubmitFn = delve(window, `__mylo__.onSubmit`, () => {
      return window.Promise.resolve(validate(values, props)).then(errors => {
        if (hasValidationErrors(errors)) {
          throw new SubmissionError(errors)
        }

        function hasValidationErrors (obj) {
          return Object.keys(obj).some(key => {
            if (typeof obj[key] === 'object') {
              return hasValidationErrors(obj[key])
            }
            return !!obj[key]
          })
        }
      })
    })
    return timedSubmit({
      submitPromise: onSubmitFn(values, dispatch, props, onSubmitArgs, window),
      ms: onSubmitArgs.timeoutMs || 10000,
      message: 'This is too slow!',
      requestId: 'timed-out'
    }).catch(err => {
      if (!(err instanceof SubmissionError)) {
        throw new SubmissionError({
          _error: err.message || err.toString(),
          _support: extractSupportMeta({
            support: onSubmitArgs.support,
            error: err,
            values
          })
        })
      }
      throw err
    })
  }
  const onSubmitFail = () => {
    setTimeout(() => {
      const fieldError = document.querySelector('.form__field.form--error')
      if (fieldError && fieldError.scrollIntoView) {
        fieldError.scrollIntoView({ behavior: 'smooth' })
      } else {
        window.scrollTo(0, 0)
      }
    }, 0)
  }
  const currentValues = delve(state, `form.${formName}.values`, {})
  return {
    currentValues,
    submitAttributes: {
      disabled: evaluateCondition({
        obj: { condition: submit.disabled || {} },
        currentValues
      })
    },
    submitText: submit.text || 'Submit',
    support: extractSupportLink(state, formName),
    confirmation: extractConfirmation(state, formName),
    form: formName,
    initialValues,
    validate,
    onSubmitSuccess () {
      window.setTimeout(() => {
        window.scrollTo(0, 0)
      }, 10)
    },
    onSubmit,
    onSubmitFail
  }
}

export default connect(mapStateToProps)(reduxForm({})(MuiBuyflow))

export function prefillValue (data, field) {
  const { from, path, fallback } = field.value
  let value
  switch (from) {
    case 'queryStringOnly':
      value = delve({ queryString: qs.parse(window.location.search) }, path)
      if (value) {
        if (value === 'false') value = false
        if (value === 'true') value = true
        if (field.name === 'phoneNumber' || field.name === 'phone') {
          value = formatPhoneNumber(value)
        }
      }
      break
    case 'queryString':
      value = delve({ queryString: qs.parse(window.location.search) }, path)
      if (!value) {
        // fallback to localStorage mylo-query-params
        const savedQueryParams = {
          queryString: storage.get('mylo-query-params') || {}
        }
        value = delve(savedQueryParams, path)
      }
      if (value === 'false') value = false
      if (value === 'true') value = true
      if (field.name === 'phoneNumber') {
        value = formatPhoneNumber(value)
      }
      break
    case 'clientOptions':
      value = delve(window, path)
      break
    case 'cookie':
      value = Cookies.get(path)
      break
  }
  dset(data, field.name, value || fallback)
  return data
}

function prefillValues (data, fields) {
  return fields.reduce((d, field) => {
    if (typeof field.value === 'object' && field.value.hasOwnProperty('from')) {
      return prefillValue(d, field)
    }
    if (field.value) {
      dset(d, field.name, field.value)
      return d
    }
    return d
  }, data)
}
